import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Heart, MapPin, Clock, Eye, Phone, ChevronLeft, ChevronRight, ShieldCheck, Share2, Flag, Loader2, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import ListingCard from "@/components/listings/ListingCard";
import { fetchListing, fetchListings } from "@/lib/api";

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isFav, toggleFavorite, categories } = useApp();
  const [listing, setListing] = useState(null);
  const [related, setRelated] = useState([]);
  const [imgIdx, setImgIdx] = useState(0);
  const [showPhone, setShowPhone] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setImgIdx(0); setShowPhone(false);
    fetchListing(id)
      .then((doc) => {
        setListing(doc);
        return fetchListings({ category: doc.category, limit: 8 });
      })
      .then((r) => setRelated((r.items || []).filter((x) => x.id !== id).slice(0, 4)))
      .catch(() => setListing(null))
      .finally(() => setLoading(false));
  }, [id]);

  const cat = listing ? categories.find((c) => c.id === listing.category) : null;

  if (loading) return <div className="py-20 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin" style={{ color: "var(--amd-primary)" }} /></div>;
  if (!listing) return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 text-center">
      <h1 className="text-2xl font-black text-white">Anunțul nu a fost găsit</h1>
      <Link to="/" className="amd-link mt-4 inline-block">← Înapoi acasă</Link>
    </div>
  );

  const fav = isFav(listing.id);
  const next = () => setImgIdx((i) => (i + 1) % listing.images.length);
  const prev = () => setImgIdx((i) => (i - 1 + listing.images.length) % listing.images.length);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8 fade-in">
      <button onClick={() => navigate(-1)} className="text-sm amd-link inline-flex items-center gap-1">
        <ChevronLeft className="w-4 h-4" /> Înapoi
      </button>

      <div className="mt-3 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="amd-card rounded-lg overflow-hidden">
            <div className="relative aspect-[4/3] sm:aspect-[16/10] bg-black">
              {listing.images.length > 0 ? (
                <img src={listing.images[imgIdx]} alt={listing.title} className="w-full h-full object-contain" />
              ) : <div className="w-full h-full grid place-items-center text-sm" style={{ color: "var(--amd-text-muted)" }}>Fără imagini</div>}
              {listing.images.length > 1 && (
                <>
                  <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full grid place-items-center bg-black/60 hover:bg-black/80"><ChevronLeft className="w-5 h-5 text-white" /></button>
                  <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full grid place-items-center bg-black/60 hover:bg-black/80"><ChevronRight className="w-5 h-5 text-white" /></button>
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-2 py-1 rounded bg-black/60 text-white text-xs">{imgIdx + 1} / {listing.images.length}</div>
                </>
              )}
            </div>
            {listing.images.length > 1 && (
              <div className="flex gap-2 p-2 overflow-x-auto" style={{ background: "var(--amd-surface-alt)" }}>
                {listing.images.map((src, i) => (
                  <button key={i} onClick={() => setImgIdx(i)} className="w-20 h-16 rounded overflow-hidden shrink-0 border-2" style={{ borderColor: i === imgIdx ? "var(--amd-primary)" : "transparent" }}>
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="amd-card rounded-lg p-4 md:p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--amd-primary)" }}>{cat?.name}{listing.subcategory ? ` • ${listing.subcategory}` : ""}</div>
                <h1 className="mt-1 text-xl md:text-3xl font-black text-white">{listing.title}</h1>
                <div className="mt-2 text-3xl md:text-4xl font-black" style={{ color: "var(--amd-primary)" }}>
                  {Number(listing.price).toLocaleString("ro-RO")} {listing.currency}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggleFavorite(listing.id)} className="w-10 h-10 rounded-md grid place-items-center amd-card" title="Favorite">
                  <Heart className={`w-5 h-5 ${fav ? "fill-current" : ""}`} style={{ color: fav ? "var(--amd-primary)" : "#fff" }} />
                </button>
                <button onClick={() => navigator.clipboard?.writeText(window.location.href)} className="w-10 h-10 rounded-md grid place-items-center amd-card" title="Distribuie">
                  <Share2 className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-4 text-sm" style={{ color: "var(--amd-text-muted)" }}>
              <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {listing.location || listing.city}</span>
              <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4" /> {new Date(listing.created_at).toLocaleDateString("ro-RO")}</span>
              <span className="inline-flex items-center gap-1"><Eye className="w-4 h-4" /> {listing.views || 0} vizualizări</span>
              <span className="px-2 py-0.5 rounded text-xs font-semibold text-white" style={{ background: "var(--amd-surface-alt)", border: "1px solid var(--amd-border)" }}>{listing.condition}</span>
              {listing.status === "sold" && <span className="px-2 py-0.5 rounded text-xs font-bold uppercase text-white" style={{ background: "#16A34A" }}>Vândut</span>}
            </div>

            <div className="my-5 h-px" style={{ background: "var(--amd-border)" }} />

            <h3 className="font-bold text-white mb-2">Descriere</h3>
            <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: "var(--amd-text-muted)" }}>{listing.description}</p>

            <button className="mt-6 text-xs inline-flex items-center gap-1" style={{ color: "var(--amd-text-muted)" }}>
              <Flag className="w-3.5 h-3.5" /> Raportează anunțul
            </button>
          </div>
        </div>

        <aside className="space-y-4">
          <div className="amd-card rounded-lg p-4">
            <Link to={listing.user_id ? `/utilizator/${listing.user_id}` : "#"} className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full grid place-items-center text-white font-black" style={{ background: "var(--amd-primary)" }}>
                {(listing.seller_name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="font-bold text-white truncate hover:underline">{listing.seller_name}</div>
                <div className="text-xs" style={{ color: "var(--amd-text-muted)" }}>Pe amd64 din {listing.seller_since || "recent"}</div>
              </div>
            </Link>
            <Button onClick={() => setShowPhone(true)} className="amd-btn-primary w-full mt-4 h-11 gap-2">
              <Phone className="w-4 h-4" />
              {showPhone ? listing.seller_phone : "Afișează numărul"}
            </Button>
            {listing.user_id && (
              <Link to={`/utilizator/${listing.user_id}`} className="mt-3 w-full inline-flex items-center justify-center gap-2 text-sm h-10 rounded-md amd-card">
                <User className="w-4 h-4" /> Vezi toate anunțurile vânzătorului
              </Link>
            )}
            <div className="mt-3 text-xs inline-flex items-center gap-2" style={{ color: "var(--amd-text-muted)" }}>
              <ShieldCheck className="w-4 h-4" style={{ color: "var(--amd-primary)" }} />
              Cumpără în siguranță — întâlnește vânzătorul în loc public.
            </div>
          </div>

          <div className="amd-card rounded-lg p-4 text-xs" style={{ color: "var(--amd-text-muted)" }}>
            <div className="font-bold text-white text-sm mb-2">Sfaturi de siguranță</div>
            <ul className="list-disc pl-4 space-y-1">
              <li>Verifică produsul înainte de plată.</li>
              <li>Nu trimite bani în avans.</li>
              <li>Întâlnește vânzătorul într-un loc aglomerat.</li>
            </ul>
          </div>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="mt-10">
          <h2 className="text-xl font-black text-white mb-3">Anunțuri similare</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {related.map((l) => <ListingCard key={l.id} listing={l} />)}
          </div>
        </section>
      )}
    </div>
  );
}
